import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild } from '@angular/core';
import { IonicModule } from '@ionic/angular';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';
import { Chart, ChartDataset, ChartOptions } from 'chart.js';
import moment from 'moment-timezone';
import { BaseChartDirective } from 'ng2-charts';

import { FilterButtonComponent } from 'src/app/components/filter-button/filter-button.component';
import {
  DATE_OPTIONS,
  DATE_RANGE_TYPES,
  CHART_DATE_DIMENSIONS,
  CONVERSATION_STATUS,
  CONVERSATION_PRIORITY_VALUES,
} from 'src/app/constants';
import { FilterOption, SelectedFilterMap, convertFiltersToReportFilterClauses } from 'src/app/utils/filter-utils';
import { getRandomColor } from 'src/app/utils/utils';
import {
  AiUsageService,
  AiUsageQuery,
  AiUsageTimeSeriesResponse,
  AiUsageMetric,
  AiUsageDimension,
} from 'src/services/ai-usage.service';
import { AuthService } from 'src/services/auth.service';
import { SearchablePopoverService } from 'src/services/searchable-popover.service';

// Metric definitions
const METRICS = {
  TOKENS: { value: AiUsageMetric.TOKEN_COUNT, label: 'Tokens' },
  REQUESTS: { value: AiUsageMetric.REQUEST_COUNT, label: 'Requests' },
} as const;

// Dimension object type
interface DimensionOption {
  label: string;
  key: string | undefined; // AiUsageDimension enum value, custom field key, or undefined for 'Overall'
  icon: string;
}

// Table row type
interface TableRow {
  dimension: string;
  dimensionLabel: string;
  total: number;
  [date: string]: number | string; // Dynamic date columns
}

@Component({
  standalone: true,
  selector: 'app-ai-usage',
  templateUrl: './ai-usage.component.html',
  styleUrls: ['./ai-usage.component.scss'],
  imports: [CommonModule, IonicModule, BaseChartDirective, NgxDatatableModule, FilterButtonComponent],
})
export class AiUsageComponent implements OnInit {
  @ViewChild('chart') chart: Chart;

  // expose constants to template
  readonly METRICS_LIST = Object.values(METRICS);
  readonly DIMENSIONS_LIST = [
    {
      label: 'Overall',
      key: undefined,
      icon: 'pulse',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      key: AiUsageDimension.CUSTOMER_ID,
      icon: this.authService.tenant.accountsEnabled ? 'chatbubble' : 'business',
    },
    {
      label: 'Submitter',
      key: AiUsageDimension.SUBMITTER_USER_ID,
      icon: 'person',
    },
    {
      label: 'Source',
      key: AiUsageDimension.SOURCE_TYPE,
      icon: 'log-in',
    },
    {
      label: 'AI Source',
      key: AiUsageDimension.TRIGGER,
      icon: 'sparkles',
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      key: AiUsageDimension.CUSTOMER_TAG_ID,
      icon: 'pricetag',
    },
    {
      label: 'Ticket Type',
      key: AiUsageDimension.TICKET_TYPE_ID,
      icon: 'ticket',
    },
    {
      label: 'Status Type',
      key: AiUsageDimension.STATUS,
      icon: 'pulse',
    },
    {
      label: 'Project',
      key: AiUsageDimension.PROJECT_ID,
      icon: 'custom-project',
    },
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          key: AiUsageDimension.ACCOUNT_ID,
          icon: 'business',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          key: AiUsageDimension.ACCOUNT_TAG_ID,
          icon: 'pricetag',
        }
      : undefined,
    {
      label: 'Custom Field',
      key: AiUsageDimension.TICKET_TYPE_FIELDS,
      icon: 'flask',
    },
  ].filter((o) => o !== undefined);

  // component state
  isLoading = false;
  selectedMetric: AiUsageMetric = METRICS.REQUESTS.value;
  selectedDimensionKey: string | undefined = undefined; // Can be an AiUsageDimension enum value or a custom field key like 'ticketTypeFields.abc123'

  // date range
  dateRange = DATE_OPTIONS[1]; // Last 30 days
  startDate: Date | null = null;
  endDate: Date | null = null;
  timezone: string;
  isDateRangeInvalid = false;

  // date grouping (day/week/month)
  dateDimension: (typeof CHART_DATE_DIMENSIONS)[keyof typeof CHART_DATE_DIMENSIONS] = CHART_DATE_DIMENSIONS.DAY;
  dateDimensionLabel = 'Daily';

  // filters
  filterOptions: FilterOption[] = [
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel' : 'Customer',
      id: AiUsageDimension.CUSTOMER_ID,
      icon: 'business',
      type: 'select',
    },
    {
      label: 'Submitter',
      id: AiUsageDimension.SUBMITTER_USER_ID,
      icon: 'person',
      type: 'select',
      searchablePopoverOptions: {
        showCurrentUserOption: true,
      },
    },
    {
      label: 'Source',
      id: AiUsageDimension.SOURCE_TYPE,
      icon: 'log-in',
      type: 'select',
      values: [
        {
          label: 'Slack',
          id: 'slack',
        },
        {
          label: 'Email',
          id: 'email',
        },
        {
          label: 'In-app',
          id: 'widget',
        },
        {
          label: 'Dashboard',
          id: 'dashboard',
        },
        {
          label: 'Microsoft Teams',
          id: 'microsoft-teams',
        },
      ],
    },
    {
      label: 'AI Source',
      id: AiUsageDimension.TRIGGER,
      icon: 'sparkles',
      type: 'select',
      values: [
        {
          label: 'Autoresponder',
          id: 'autoresponder',
        },
        {
          label: 'Agent',
          id: 'agent',
        },
        {
          label: 'Suggested Response',
          id: 'suggested-response',
        },
        {
          label: 'Portal AI Assistant',
          id: 'portal-ai-assistant',
        },
        {
          label: 'Query Knowledge Base',
          id: 'query-knowledge-base',
        },
        {
          label: 'AI Tester',
          id: 'ai-tester',
        },
        {
          label: 'Automation',
          id: 'automation',
        },
      ],
    },
    {
      label: this.authService.tenant.accountsEnabled ? 'Channel Tag' : 'Customer Tag',
      id: AiUsageDimension.CUSTOMER_TAG_ID,
      searchId: 'customer.tags.id',
      icon: 'pricetag',
      type: 'select',
    },
    {
      label: 'Ticket Type',
      id: AiUsageDimension.TICKET_TYPE_ID,
      icon: 'ticket',
      type: 'select',
    },
    {
      label: 'Status Type',
      id: AiUsageDimension.STATUS,
      icon: 'pulse',
      type: 'select',
    },
    !this.authService.selectedProject
      ? {
          label: 'Project',
          id: AiUsageDimension.PROJECT_ID,
          icon: 'custom-project',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account',
          id: AiUsageDimension.ACCOUNT_ID,
          icon: 'business',
          type: 'select',
        }
      : undefined,
    this.authService.tenant.accountsEnabled
      ? {
          label: 'Account Tag',
          id: AiUsageDimension.ACCOUNT_TAG_ID,
          icon: 'pricetag',
          type: 'select',
        }
      : undefined,
    {
      label: 'Custom Field',
      id: AiUsageDimension.TICKET_TYPE_FIELDS,
      icon: 'flask',
      isCustomFieldGroup: true,
      type: 'variable',
    },
  ].filter((o) => o !== undefined) as FilterOption[];
  filters: SelectedFilterMap = new Map();

  // chart data
  chartData: ChartDataset[] = [];
  chartLabels: string[] = [];
  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true },
    },
    scales: {
      y: { beginAtZero: true },
    },
  };

  // table data
  tabularData: TableRow[] = [];
  columns: string[] = [];
  lookups: Record<string, Record<string, string>> = {};

  constructor(
    private aiUsageService: AiUsageService,
    private searchablePopoverService: SearchablePopoverService,
    public authService: AuthService,
  ) {}

  async ngOnInit() {
    this.timezone = moment.tz.guess();
    this.endDate = moment().toDate();
    this.startDate = moment(this.endDate).subtract(30, 'days').toDate();

    await this.loadData();
  }

  /**
   * Build query from current state
   */
  private buildQuery(): AiUsageQuery {
    const query: AiUsageQuery = {
      metric: this.selectedMetric,
      dateDimension: this.dateDimension,
      timezone: this.timezone,
      dateRange:
        this.dateRange.type === DATE_RANGE_TYPES.RELATIVE
          ? {
              type: DATE_RANGE_TYPES.RELATIVE,
              period: this.dateRange.period as (typeof CHART_DATE_DIMENSIONS)[keyof typeof CHART_DATE_DIMENSIONS],
              startIndex: this.dateRange.startIndex,
              endIndex: this.dateRange.endIndex,
              label: this.dateRange.label,
            }
          : {
              type: DATE_RANGE_TYPES.ABSOLUTE,
              startDate: moment(this.startDate).format('YYYY-MM-DD'),
              endDate: moment(this.endDate).format('YYYY-MM-DD'),
            },
    };

    if (this.selectedDimensionKey) {
      query.dimensions = [this.selectedDimensionKey];
    }

    // Convert UI filters to filter clauses
    if (this.filters.size > 0) {
      query.filters = convertFiltersToReportFilterClauses(this.filters);
    }

    return query;
  }

  /**
   * Load data from service and update chart/table
   */
  async loadData() {
    this.isLoading = true;

    try {
      const query = this.buildQuery();
      const response = await this.aiUsageService.getAiUsageTimeSeries(query);

      this.updateChartData(response);
      this.updateTableData(response);
    } catch (error) {
      console.error('Failed to load AI usage data:', error);
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Transform API response into chart format
   */
  private updateChartData(response: AiUsageTimeSeriesResponse) {
    this.chartLabels = response.dates.map((date) => moment(date).format('MMM D'));
    this.lookups = response.lookups;

    const datasets: ChartDataset[] = [];

    Object.keys(response.data).forEach((key) => {
      const values = response.dates.map((date) => response.data[key][date] || 0);
      const label = this.getDimensionValueLabel(key);
      const color = this.getColor(key);

      datasets.push({
        label,
        data: values,
        borderColor: color,
        backgroundColor: (ctx: any) => {
          // Create gradient for fill
          const grad = ctx.chart.ctx.createLinearGradient(0, 0, 0, 600);
          grad.addColorStop(0, color);
          grad.addColorStop(1, '#ffffff00');
          return grad;
        },
        fill: true,
        tension: 0.3, // Smooth lines
      } as ChartDataset);
    });

    this.chartData = datasets;
  }

  /**
   * Get color for a dimension value (matches Performance tab color logic)
   */
  private getColor(name: string): string {
    // Color map for specific known values
    const colorMap: Record<string, string> = {
      // Status colors
      [CONVERSATION_STATUS.OPEN]: '#cc0022aa',
      [CONVERSATION_STATUS.IN_PROGRESS]: '#cc9922aa',
      [CONVERSATION_STATUS.ON_HOLD]: '#0000ffaa',
      [CONVERSATION_STATUS.CLOSED]: '#00cc22aa',
      // Priority colors
      [CONVERSATION_PRIORITY_VALUES.LOW]: '#00cc22aa',
      [CONVERSATION_PRIORITY_VALUES.MEDIUM]: '#0000ccaa',
      [CONVERSATION_PRIORITY_VALUES.HIGH]: '#cc9922aa',
      [CONVERSATION_PRIORITY_VALUES.CRITICAL]: '#cc0022aa',
      // Source type colors
      slack: '#4A154B',
      email: '#EA4335',
      widget: '#0084FF',
      dashboard: '#6B46C1',
      'microsoft-teams': '#5059C9',
      // Null/undefined
      null: '#88888855',
    };

    return colorMap[name] ?? getRandomColor();
  }

  /**
   * Transform API response into table format
   */
  private updateTableData(response: AiUsageTimeSeriesResponse) {
    this.columns = response.dates;

    this.tabularData = Object.keys(response.data).map((key) => {
      const row: TableRow = {
        dimension: key,
        dimensionLabel: this.getDimensionValueLabel(key),
        total: 0,
      };

      response.dates.forEach((date) => {
        const value = response.data[key][date] || 0;
        row[date] = value;
        row.total += value;
      });

      return row;
    });
  }

  /**
   * Get human-readable label for a specific dimension value (e.g., convert customerId to customer name)
   */
  private getDimensionValueLabel(key: string): string {
    if (!this.selectedDimensionKey) {
      return this.getMetricLabel();
    }

    const lookup = this.lookups[this.selectedDimensionKey];
    return lookup?.[key] || key;
  }

  /**
   * Handle metric selection change
   */
  async selectMetric(event: any) {
    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: false,
      callback: (id: string) => {
        // Validate that id is a valid AiUsageMetric
        const validMetrics = Object.values(AiUsageMetric) as string[];
        if (validMetrics.includes(id)) {
          this.selectedMetric = id as AiUsageMetric;
          this.loadData();
        } else {
          console.warn(`Invalid metric selected: ${id}, falling back to default`);
          this.selectedMetric = AiUsageMetric.TOKEN_COUNT;
          this.loadData();
        }
      },
      loadData: async () => {
        return this.METRICS_LIST.map((m) => ({
          id: m.value,
          title: m.label,
          object: m,
        }));
      },
    });
  }

  /**
   * Handle dimension change
   */
  setDimension(dimension: DimensionOption) {
    this.selectedDimensionKey = dimension.key;
    this.loadData();
  }

  /**
   * Handle date range change
   */
  async showDateRangeOptions(event: any) {
    this.searchablePopoverService.dateValues({
      event,
      callback: (_id: string, _name: string, dateRange: any) => {
        this.dateRange = dateRange;
        this.loadData();
      },
    });
  }

  /**
   * Handle date dimension change (day/week/month)
   */
  async showDateDimensionOptions(event: any) {
    const options = [
      { id: CHART_DATE_DIMENSIONS.DAY, label: 'Daily' },
      { id: CHART_DATE_DIMENSIONS.WEEK, label: 'Weekly' },
      { id: CHART_DATE_DIMENSIONS.MONTH, label: 'Monthly' },
    ];

    this.searchablePopoverService.showPopover({
      event,
      showImages: false,
      showSearch: false,
      callback: (id: string) => {
        // Validate that id is a valid CHART_DATE_DIMENSIONS value
        const validDimensions = Object.values(CHART_DATE_DIMENSIONS) as string[];
        if (validDimensions.includes(id)) {
          this.dateDimension = id as (typeof CHART_DATE_DIMENSIONS)[keyof typeof CHART_DATE_DIMENSIONS];
          this.dateDimensionLabel = options.find((o) => o.id === id)?.label || 'Daily';
          this.loadData();
        } else {
          console.warn(`Invalid date dimension selected: ${id}, falling back to daily`);
          this.dateDimension = CHART_DATE_DIMENSIONS.DAY;
          this.dateDimensionLabel = 'Daily';
          this.loadData();
        }
      },
      loadData: async () => {
        return options.map((o) => ({
          id: o.id,
          title: o.label,
          object: o,
        }));
      },
    });
  }

  /**
   * Handle filter changes
   */
  filtersDidChange(filters: SelectedFilterMap) {
    this.filters = filters;
    this.loadData();
  }

  getMetricLabel(): string {
    return this.METRICS_LIST.find((m) => m.value === this.selectedMetric)?.label || 'Unknown';
  }

  isDimensionActive(dimension: DimensionOption): boolean {
    return this.selectedDimensionKey === dimension.key;
  }

  getColumnName(date: string): string {
    return moment(date).format('MMM D');
  }

  /**
   * Get the label for the currently selected dimension type (e.g., "Channel", "Submitter")
   * Used for table column headers
   */
  getDimensionLabel(): string {
    if (!this.selectedDimensionKey) {
      return this.getMetricLabel();
    }
    const dimension = this.DIMENSIONS_LIST.find((d) => d.key === this.selectedDimensionKey);
    return dimension?.label || this.selectedDimensionKey;
  }
}
